import * as React from "react";
import { connect } from "react-redux";
import { CookiesProvider, useCookies } from "react-cookie";
import { BrowserRouter, Route, Switch, useHistory } from "react-router-dom";
import BloodGlucoseListScreen from "../BloodGlucose/BloodGlucoseListScreen";
import { CoughSessionsListScreen } from "../cough/SessionsList/CoughSessionsListScreen";
import { MultiStudyBrickwall } from "../misc/components/MultiStudyBrickwall";
import { MainState } from "./reducers";
import { DevicesScreen } from "../Devices/DevicesScreen";
import { UploadFileModal } from "../Devices/UploadRecordingFileModal";
import DataCorrectionRequestForm from "../DataCorrectionFacility/Request/DataCorrectionRequestForm";
import { DataCorrectionRequestView } from "../DataCorrectionFacility/View/DataCorrectionRequestView";
import { NoStudyBrickwall } from "../misc/components/NoStudyBrickwall";
import ReportsApp from "../reports/ReportsApp";
import ReportViewerScreen from "../reports/ReportViewerScreen";
import { User } from "../misc/api/apiTypes";
import { AlertNotificationList } from '../alert-notification-list/AlertNotificationList';
import AdminMenuLayout from "../Administration/_AdminMenuLayout";
import EcgSessionScreen from '../Ecg/EcgSessionListScreen';
import { AuditTrailScreen } from "../AuditTrail/AuditTrailScreen";
import { FixedCommentsProvider } from './FixedCommentsContext';
import { SpirometryRoot } from "../PulmonaryFunctionTests/Spirometry/SpirometryRoot";
import { SvcRoot } from "../PulmonaryFunctionTests/Svc/SvcRoot";
import { DlcoRoot } from "../PulmonaryFunctionTests/Dlco/DlcoRoot";
import { SubjectsScreen } from "../Subjects/SubjectsScreen";
import { ReferenceContextProvider } from "./ReferenceContextProvider";
import { CurrentStudyConfigurationContextProvider } from "./CurrentStudyConfigurationContext";
import { SecurityContextProvider } from "./SecurityContext";
import { DiaryRoot } from "../EDiary/DiaryRoot";
import { Unauthorized } from "../errors/unauthorized/Unauthorized";
import { DcfSettings } from "../Administration/Studies/Settings/DcfSettings";
import { DirectoryVisualizer } from "../directory-visualizer/DirectoryVisualizer";
import { SelectStudy } from "../SelectStudy";
import { Dashboard } from '../dashboard/components/dashboard';
import { SupportDocuments } from "../Support/SupportDocuments";
import { SupportContactDetails } from "../Support/SupportContactDetails";
import { DataCorrectionListAndViewScreen } from "../DataCorrectionFacility/DataCorrectionListAndViewScreen";
import { SessionsListScreen } from "../Sessions/SessionsListScreen";
import { BloodGlucoseTestsScreen } from "../BloodGlucose/BloodGlucoseTestsScreen";
import { Study } from "webportalclient";
import { UnderApproval } from "../Account/UnderApproval";
import { TopNavbar } from "./TopNavbar";
import { AccountSettings } from "../Account/AccountSettings";
import { NoUser } from "../Account/NoUser";
import { LockedOut } from "../Account/LockedOut";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { SignedOut } from "../Account/SignedOut";
import { SignIn } from "../Account/SignIn";
import { GetStudyAction, LoadCurrentUserAction } from "./SecurityActions";
import { CoughUploadReport } from "../cough/UploadReport/CoughUploadReport";
import { PefRoot } from "../PulmonaryFunctionTests/Pef/PefRoot";
import { SurveyView } from "../Surveys/SurveyView";
import { MepRoot } from "../PulmonaryFunctionTests/Pressure/MEP/MepRoot";
import { MipRoot } from "../PulmonaryFunctionTests/Pressure/MIP/MipRoot";
import { SnipRoot } from "../PulmonaryFunctionTests/Pressure/SNIP/SnipRoot";
import { LungVolumeRoot } from "../PulmonaryFunctionTests/LungVolume/LungVolumeRoot";
import { OscillometryRoot } from "../PulmonaryFunctionTests/Oscillometry/OscillometryRoot";
import { EcgRoot } from "../Ecg/EcgRoot";
import { PrivateRoute } from "./PrivateRoute";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";

export interface CoreAppProps {
    currentUser: User;
    currentStudy: Study;
    isNoStudies: boolean;
    version: string;
    logoutRedirectUri: string;

    dispatch: any;
}

export const CoreApp = (props: CoreAppProps) => {
    const [cookies] = useCookies(['WpCurrentStudy']);
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const [currentUser, setCurrentUser] = React.useState<User>();
    const [isMultiStudy, setIsMultiStudy] = React.useState<boolean>();
    const [isNoStudies, setIsNoStudies] = React.useState<boolean>();
    const [activeStudy, setActiveStudy] = React.useState<Study>();
    const [studyChange, setStudyChange] = React.useState<string>();
    const [isAllStudies, setIsAllStudies] = React.useState<boolean>(false);
    const history = useHistory();

    React.useEffect(() => {
        const init = async () => {
            const currentStudyId = cookies.WpCurrentStudy;
            try {
                const userResult = await legacyClient.getCurrentUser();
                setCurrentUser(userResult);
                props.dispatch(LoadCurrentUserAction.create(userResult));   // Legacy study access support

                const studiesResult = await client.client.getStudies(undefined, undefined, "name asc");

                if (studiesResult.value.length === 0) {
                    setIsMultiStudy(true);
                    setIsNoStudies(true);
                    return;
                }

                const currentStudy = studiesResult.value.find(x => x.id === currentStudyId);

                if (!currentStudy)
                    setIsMultiStudy(true);
                else
                    setIsMultiStudy(false);

                setActiveStudy(currentStudy);
                props.dispatch(GetStudyAction.create(currentStudy?.id)); // Legacy study access support
            } catch (error) {
                if (error === "User requires approval")
                    history.push("/underapproval");
                else
                    history.push("/unauthorized");
            }
        }

        if (studyChange == "All Studies") {
            setIsMultiStudy(true);
            setIsNoStudies(true);
            setIsAllStudies(true)
            return;
        }

        init();
    }, [studyChange]);

    if (currentUser) {
        if (isMultiStudy) {
            return (
                <SecurityContextProvider user={currentUser}>
                    <ReferenceContextProvider version={props.version} logoutRedirectUri={props.logoutRedirectUri}>
                        <TopNavbar isMultiStudy={isMultiStudy} currentStudy={activeStudy} />
                        <Switch>
                            <Route path="/Users/:didEmailFail?/:isSaveMessageDisplayed?" component={AdminMenuLayout} />
                            <Route path="/EDiaryConfigurations" component={AdminMenuLayout} />
                            <Route path="/Audit" component={AuditTrailScreen} />
                            <Route path="/admin" component={AdminMenuLayout} />
                            <Route path="/studies" component={AdminMenuLayout} />
                            <Route path="/requests" component={AdminMenuLayout} />
                            <Route path="/underapproval" component={UnderApproval} />
                            <Route path="/account/study" render={() => <SelectStudy setStudyChange={setStudyChange} />} />
                            <Route path="/account/settings" component={AccountSettings} />
                            <Route path="/cough/uploadreport/:reportId" component={CoughUploadReport} />
                            {isAllStudies ? <Route component={MultiStudyBrickwall} /> : <Route render={() => <SelectStudy setStudyChange={setStudyChange} />} />}
                        </Switch>
                    </ReferenceContextProvider>
                </SecurityContextProvider>
            );
        } else if (activeStudy) {
            return (
                <SecurityContextProvider user={currentUser}>
                    <ReferenceContextProvider version={props.version} logoutRedirectUri={props.logoutRedirectUri}>
                        <CurrentStudyConfigurationContextProvider currentStudy={activeStudy}>
                            <FixedCommentsProvider currentStudy={activeStudy}>
                                <TopNavbar isMultiStudy={false} currentStudy={activeStudy} />
                                <Switch>
                                    <Route exact path="/" component={Dashboard} />
                                    <Route path="/Audit" component={AuditTrailScreen} />
                                    <Route path="/Subjects" component={SubjectsScreen} />

                                    <Route path="/bloodglucose/tests" component={BloodGlucoseTestsScreen} />
                                    <Route path="/bloodglucose" component={BloodGlucoseListScreen} />

                                    <Route path="/Ecg" component={EcgRoot} />

                                    <Route path="/cough/uploadreport/:reportId" component={CoughUploadReport} />
                                    <Route path="/Cough" component={CoughSessionsListScreen} />
                                    <Route path="/Devices" component={DevicesScreen} />
                                    <Route path="/upload" component={UploadFileModal} />
                                    <PrivateRoute path="/Dcf/Request" permissions={[PermissionEnum.RequestDCF]}>
                                        <DataCorrectionRequestForm />
                                    </PrivateRoute>
                                    <PrivateRoute path="/Dcf" exact  permissions={[PermissionEnum.AccessDataCorrections]}>
                                        <DataCorrectionListAndViewScreen />
                                    </PrivateRoute>
                                    <PrivateRoute path="/Dcf/View/:dcfRequestId" permissions={[PermissionEnum.AccessDataCorrections]}>
                                        <DataCorrectionRequestView />
                                    </PrivateRoute>

                                    <Route path="/Spirometry" component={SpirometryRoot} />

                                    <Route path="/Svc" component={SvcRoot} />

                                    <Route path="/Dlco" component={DlcoRoot} />
                                    <Route path="/LungVolume" component={LungVolumeRoot} />

                                    <Route path="/Mip" component={MipRoot} />
                                    <Route path="/Mep" component={MepRoot} />
                                    <Route path="/Snip" component={SnipRoot} />

                                    <Route path="/Pef" component={PefRoot} />

                                    <Route path="/Reports" component={ReportsApp} exact />
                                    <Route path="/Reports/Viewer/:path" component={ReportViewerScreen} />
                                    <Route path="/Alerts" component={AlertNotificationList} />
                                    <Route exact path="/users/create" component={AdminMenuLayout} />
                                    <Route path="/users/edit/:id" component={AdminMenuLayout} />
                                    <Route path="/users" component={AdminMenuLayout} />
                                    <Route path="/requests" component={AdminMenuLayout} />
                                    <Route path="/RoleManagement" component={AdminMenuLayout} />
                                    <Route path="/VisitSchedule/:studyId" component={AdminMenuLayout} />
                                    <Route path="/StudySettings" component={AdminMenuLayout} />
                                    <Route path="/FixedComments" component={AdminMenuLayout} />

                                    <Route path="/DiaryConfigurations" component={AdminMenuLayout} />
                                    <Route path="/UpdatePackages" component={AdminMenuLayout} />
                                    <Route path="/Diary" component={DiaryRoot} />

                                    <Route path="/deviceadministration" component={AdminMenuLayout} />

                                    <Route path="/sessions" component={SessionsListScreen} exact />

                                    <Route path="/Studies/DcfSettings/:id" component={DcfSettings} />

                                    <Route path="/Studies/FvcSettings/:id" component={AdminMenuLayout} />
                                    <Route path="/Studies/DiarySettings/:id" component={AdminMenuLayout} />
                                    <Route path="/Studies/MipSettings/:id" component={AdminMenuLayout} />

                                    <Route path="/unauthorized" component={Unauthorized} />
                                    <Route path="/sharedfiles" component={DirectoryVisualizer} />
                                    <Route path="/underapproval" component={UnderApproval} />

                                    <Route path="/support/documents" component={SupportDocuments} />
                                    <Route path="/support/details" component={SupportContactDetails} />

                                    <Route path="/sites" component={AdminMenuLayout} />
                                    <Route path="/admin" component={AdminMenuLayout} />
                                    <Route path="/admin/editcontact" component={AdminMenuLayout} />
                                    <Route path="/studies" component={AdminMenuLayout} />

                                    <Route path="/Oscillometry" component={OscillometryRoot} />

                                    <Route path="/serviceresources" component={AdminMenuLayout} />

                                    <Route path="/Surveys/:id" component={SurveyView} />

                                    <Route path="/account/study" render={() => <SelectStudy setStudyChange={setStudyChange} />} />
                                    <Route path="/account/settings" component={AccountSettings} />
                                </Switch>
                            </FixedCommentsProvider>
                        </CurrentStudyConfigurationContextProvider>
                    </ReferenceContextProvider>
                </SecurityContextProvider>
            );
        }
    }
    if (isNoStudies) {
        return <NoStudyBrickwall />;
    }

    return (
        <Switch>
            <Route exact path="/unauthorized" component={Unauthorized} />
            <Route exact path="/underapproval" render={() => <UnderApproval logoutRedirectUri={props.logoutRedirectUri} />} />
            <Route exact path="/account/nouser" component={NoUser} />
            <Route exact path="/account/lockedout" component={LockedOut} />
            <Route exact path="/account/signedout" component={SignedOut} />
            <Route component={SignIn} />
        </Switch>
    );
}



function mapStateToProps(state: MainState) {
    return {
        currentStudy: state.core.currentStudy,
        currentUser: state.security.currentUser,
        isNoStudies: state.security.isNoStudies
    };
}

export default connect(mapStateToProps)(CoreApp);
